'use strict';

// external
import {keys} from 'ts-transformer-keys';
import {enumerate} from 'ts-transformer-enumerate';

// shared imports
import {ItemAttributes} from '~/shared/src/ci/Item';
import {ReferenceAttributes} from '~/shared/src/ci/Reference';
import {PropertyValueType} from '~/shared/src/mm/PropertySet';
import {BaseRequestParameters} from '~/shared/src/model/BaseRequest';
import {
    DataDescriptors,
    DataStructure
} from '~/shared/src/utilities/dataStructure';
import {Action} from '~/shared/src/utilities/action';
import {Format} from '~/shared/src/utilities/format';
import {Function} from '~/shared/src/utilities/function';
import {LayerType as MapLayerType} from '~/shared/src/utilities/map';
import {Message} from '~/shared/src/utilities/message';
import {Query} from '~/shared/src/utilities/query';
import {Style} from '~/shared/src/utilities/style';
import {Validator} from '~/shared/src/utilities/validator';
import {
    LoadedItemData,
    LoadedReferenceData
} from '~/shared/src/utilities/loadedData';
import {
    ColumnConfig,
    ColumnFilter,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';
import {
    LoadFinderOverviewDataParameters,
    LoadFinderOverviewDataResult
} from '~/shared/src/utilities/modules/finder';


/*** type definitions ************************************************************************************************/

type RequiredKeys<T> = { [K in keyof T]-? : {} extends Pick<T, K> ? never : K }[keyof T];
type RequiredOf<T> = Pick<T, RequiredKeys<T>>;

type Parameters = {
    [ parameterKey : string ] : any;
};

/**
 * Applies the (already inherited) `accessType` of a layoutElement to its `readOnly` / `visibility` properties.
 *
 * Has to be called *after* the properties to be inherited to the children have been picked - otherwise the
 * `readOnly` / `visibility` derived from the accessType would be inherited to children which widen their own
 * accessType again (`Group - [ hasAccess ] -> <layoutElement>` may be overwritten downwards).
 *
 * @param {Object} properties
 *
 * @return {Object} the given properties (mutated)
 */
export const applyAccessType = <T extends {
    accessType? : AccessType;
    readOnly? : boolean;
    visibility? : Visibility;
}>( properties : T ) : T => {
    if ( properties.accessType === 'none' ) {
        properties.visibility = 'hidden';
        properties.readOnly = true;
    }
    else if ( properties.accessType === 'readOnly' ) {
        properties.readOnly = true;
    }

    return properties;
};

/**
 * The operation a value change results in - derived from the original value and the new one:
 *
 * | original | new     | operation |
 * |----------|---------|-----------|
 * | empty    | filled  | insert    |
 * | filled   | changed | update    |
 * | filled   | empty   | delete    |
 */
export type AccessOperation = 'update' | 'insert' | 'delete';

/**
 * Whether the given (already inherited) accessType allows the given operation.
 *
 * Undefined means "not restricted" - neither the layoutElement itself nor one of its ancestors carries
 * `Group - [ hasAccess ] -> <layoutElement>` references.
 *
 * @param {String} [accessType]
 * @param {String} operation
 *
 * @return {Boolean}
 */
export const isAccessTypeSufficient = ( accessType : AccessType | undefined, operation : AccessOperation ) : boolean =>
    accessType === undefined || accessTypeOrder[ accessType ] >= accessTypeOrder[ operation ];

/*** config definitions ********************************************************************************************/

export const useHookForm = true;
export const useHookFormDelay = 500;


/*** MetaModel types *************************************************************************************************/

export type AccessType = 'none' | 'readOnly' | 'update' | 'insert' | 'delete';
export type Alignment = 'left' | 'center' | 'right';
export type InfoTextLayout = 'hidden' | 'icon' | 'text';
export type LabelPosition = 'top' | 'left' | 'bottom' | 'right' | 'float' | 'placeholder';
export type Visibility = 'visible' | 'placeholder' | 'hidden';
export type WidthUnit = 'gridunit' | 'pixel' | 'percent' | 'rem';
export const accessTypeValueMapping : { [ value : string ] : AccessType } = {
    1: 'readOnly',
    2: 'update',
    3: 'insert',
    4: 'delete'
};
export const accessTypeOrder : { [ accessType in AccessType ] : number } = {
    none    : 0,
    readOnly: 1,
    update  : 2,
    insert  : 3,
    delete  : 4
};
export const widthUnitMapping : { [widthUnit in WidthUnit] : string } = {
    gridunit: 'gridunit',
    pixel   : 'px',
    percent : '%',
    rem     : 'rem'
};

export type CommonProperties = {
    id : number;
    key : string;
    name : string;
};


/*** properties inherited from Form down to Containers and FormFields ***/
export type FormInheritedProperties = {
    /**
     * Resolved from the `Group - [ hasAccess ] -> <layoutElement>` references of the layoutElement for the current user.
     *
     * Undefined means "not restricted at this layoutElement" - only then it is inherited from the parent,
     * a set value always wins over the inherited one (which allows to widen the access again).
     */
    accessType? : AccessType;
    infoTextLayout : InfoTextLayout
    labelAlignment : Alignment;
    labelPosition : LabelPosition;
    labelVisibility : Visibility;
    labelWidth : number;
    readOnly : boolean;
    widthUnit : WidthUnit;
    maxWidthUnit : WidthUnit;
    minWidthUnit : WidthUnit;
};
export const formInheritedProperties = keys<FormInheritedProperties>() as string[];

/*** properties inherited from Container down to Containers and FormFields ***/
export type ContainerCommonInheritedProperties = {
    visibility : Visibility;
};
export const containerCommonInheritedProperties = keys<ContainerCommonInheritedProperties>() as string[];

/*** properties inherited from Container down to Containers ***/
export type ContainerToContainerInheritedProperties = {
    titleAlignment : Alignment;
    titleVisibility : Visibility;
};
export const containerToContainerInheritedProperties = keys<ContainerToContainerInheritedProperties>() as string[];
export type ContainerInheritedProperties =
    FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties;
export const containerInheritedProperties = keys<ContainerInheritedProperties>() as string[];

/*** properties inherited from Container down to FormFields ***/
export type ContainerToFormFieldInheritedProperties = {};
export const containerToFormFieldInheritedProperties = keys<ContainerToFormFieldInheritedProperties>() as string[];
export type FormFieldInheritedProperties =
    FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties
    & ContainerToFormFieldInheritedProperties;
export const formFieldInheritedProperties = keys<FormFieldInheritedProperties>() as string[];

/*** properties common for FormFields ***/
export type CommonFormFieldProperties = {
    id : number;
    key : string;
    name : string;

    alignment : Alignment;
    attributePath? : string;
    autoComplete : boolean;
    autoCompleteDelay : number;
    autoCompleteMinLength : number;
    autoCompleteQuery? : Query;
    defaultValue? : AttributeValue;
    maxWidth? : number;
    minWidth? : number;
    order : number;
    required : boolean;
    tabOrder? : number; // defaults to parent order * 1000 + order
    width? : number;

    actions? : Action[];
    validators? : Validator[];
    format? : Format;
    images? : {};
    messages? : {
        helpText? : string;
        label? : string;
        placeholder? : string;
        tooltip? : string;
    };
    styles? : {
        style? : Style[];
        labelStyle? : Style[];
    };
};
export const commonFormFieldProperties = keys<CommonFormFieldProperties>() as string[];
export const commonFormFieldImages = keys<Required<CommonFormFieldProperties>[ 'images' ]>() as string[];
export const commonFormFieldMessages = keys<Required<CommonFormFieldProperties>[ 'messages' ]>() as string[];
export const commonFormFieldStyles = keys<Required<CommonFormFieldProperties>[ 'styles' ]>() as string[];
export const requiredCommonFormFieldProperties = keys<RequiredOf<CommonFormFieldProperties>>() as string[];

/*** CFG:Form ***/
type FormOnlyProperties = {
    gridSize : number;
    maxWidth : number;
    minWidth : number;
    showToolbar? : boolean;

    exportRequestCommand? : string;
    exportRequestModule? : string;
    exportRequestParams? : {
        [ key : string ] : string;
    };

    loadRequestCommand : string;
    loadRequestModule : string;
    loadRequestParams? : {
        [ key : string ] : string;
    };

    saveRequestCommand : string;
    saveRequestModule : string;
    saveRequestParams? : {
        [ key : string ] : string;
    };

    actions? : Action[];
    functions? : Function[];
    images? : {
        icon? : string;
    };
    messages? : {
        title? : string;
        tabTitle? : string;
        tooltipTitle? : string;
    };
};
export type FormProperties = CommonProperties & FormInheritedProperties & FormOnlyProperties;
export type LoadFormProperties = CommonProperties & Partial<FormInheritedProperties> & Partial<FormOnlyProperties>;
export const formImages = keys<Required<FormProperties>[ 'images' ]>() as string[];
export const formMessages = keys<Required<FormProperties>[ 'messages' ]>() as string[];

/*** CFG:Container ***/
type ContainerOnlyProperties = {
    disableTab? : boolean;
    isLayoutWrapper : boolean;
    maxWidth? : number;
    minWidth? : number;
    order : number;
    width? : number;

    repeatConfig? : {
        dataDescriptorAlias : string;
        rootItemId : number;
        referenceSetId : number;
        referenceDirection? : Omit<ReferenceDirection, 'Both'>;
        referenceIds? : number[];
        newReferenceId? : number;
    }
    images? : {};
    messages? : {
        title? : string | Message;
    };
    styles? : {
        style? : Style[];
        titleStyle? : Style[];
    };
};
export type ContainerProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties
    & ContainerOnlyProperties;
export type LoadContainerProperties =
    CommonProperties
    & Partial<FormInheritedProperties>
    & Partial<ContainerCommonInheritedProperties>
    & Partial<ContainerToContainerInheritedProperties>
    & Partial<ContainerOnlyProperties>;
export const containerImages = keys<Required<ContainerProperties>[ 'images' ]>() as string[];
export const containerMessages = keys<Required<ContainerProperties>[ 'messages' ]>() as string[];
export const containerStyles = keys<Required<ContainerProperties>[ 'styles' ]>() as string[];

/*** CFG:TabContainer ***/
type TabContainerOnlyProperties = {
    activeTabIndex? : number;
    distributeTabsEvenly? : boolean;
    maxWidth? : number;
    minWidth? : number;
    order : number;
    width? : number;

    actions? : Action[];
    images? : {};
    messages? : {
        placeholder? : string;
        title? : string;
    };
    styles? : {};
};
export type TabContainerProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties
    & TabContainerOnlyProperties;
export type LoadTabContainerProperties =
    CommonProperties
    & Partial<FormInheritedProperties>
    & Partial<ContainerCommonInheritedProperties>
    & Partial<ContainerToContainerInheritedProperties>
    & Partial<TabContainerOnlyProperties>;
export const tabContainerImages = keys<Required<TabContainerProperties>[ 'images' ]>() as string[];
export const tabContainerMessages = keys<Required<TabContainerProperties>[ 'messages' ]>() as string[];
export const tabContainerStyles = keys<Required<TabContainerProperties>[ 'styles' ]>() as string[];

/*** field like views ***/
/*** CFG:CalendarView ***/
export type CalendarViewProperties = CommonProperties
    & Omit<FormInheritedProperties, 'infoTextLayout' | 'labelAlignment' | 'labelPosition' | 'labelVisibility' | 'labelWidth'>
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'alignment' | 'attributePath' | 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'required' | 'tabOrder' | 'actions' | 'validators' | 'format' | 'messages'>
    & {
    type : 'CALENDAR';

    viewKey : string;

    additionalParameters? : Parameters;
    alignment? : never;
    attributePath? : never;
    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    height : number;
    maxHeight : number;
    required? : never;
    tabOrder? : never;

    actions? : never;
    validators? : never;
    format? : never;
    images? : {};
    messages? : {
        title? : string;
        placeholder? : never;
    };
};
export const calendarViewProperties = keys<CalendarViewProperties>() as string[];
export const calendarViewMessages = keys<Required<CalendarViewProperties>[ 'messages' ]>() as string[];
export const requiredCalendarViewProperties = keys<RequiredOf<CalendarViewProperties>>() as string[];

/*** CFG:ChartView ***/
export type ChartViewProperties = CommonProperties
    & Omit<FormInheritedProperties, 'infoTextLayout' | 'labelAlignment' | 'labelPosition' | 'labelVisibility' | 'labelWidth'>
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'alignment' | 'attributePath' | 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'required' | 'tabOrder' | 'actions' | 'validators' | 'format' | 'messages'>
    & {
    type : 'CHART';

    viewKey : string;

    additionalParameters? : Parameters;
    alignment? : never;
    attributePath? : never;
    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    height : number;
    maxHeight : number;
    required? : never;
    tabOrder? : never;

    actions? : never;
    validators? : never;
    format? : never;
    images? : {};
    messages? : {
        title? : string;
        placeholder? : never;
    };
};
export const chartViewProperties = keys<ChartViewProperties>() as string[];
export const chartViewMessages = keys<Required<ChartViewProperties>[ 'messages' ]>() as string[];
export const requiredChartViewProperties = keys<RequiredOf<ChartViewProperties>>() as string[];

/*** CFG:GraphView ***/
export type GraphViewProperties = CommonProperties
    & Omit<FormInheritedProperties, 'infoTextLayout' | 'labelAlignment' | 'labelPosition' | 'labelVisibility' | 'labelWidth'>
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'alignment' | 'attributePath' | 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'required' | 'tabOrder' | 'actions' | 'validators' | 'format' | 'messages'>
    & {
    type : 'GRAPH';

    viewKey : string;

    additionalParameters? : Parameters;
    alignment? : never;
    attributePath? : never;
    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    height : number;
    maxHeight : number;
    required? : never;
    tabOrder? : never;

    actions? : never;
    validators? : never;
    format? : never;
    images? : {};
    messages? : {
        title? : string;
        placeholder? : never;
    };
};
export const graphViewProperties = keys<GraphViewProperties>() as string[];
export const graphViewMessages = keys<Required<GraphViewProperties>[ 'messages' ]>() as string[];
export const requiredGraphViewProperties = keys<RequiredOf<GraphViewProperties>>() as string[];

/*** CFG:MapView ***/
export type MapViewProperties = CommonProperties
    & Omit<FormInheritedProperties, 'infoTextLayout' | 'labelAlignment' | 'labelPosition' | 'labelVisibility' | 'labelWidth'>
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'alignment' | 'attributePath' | 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'required' | 'tabOrder' | 'actions' | 'validators' | 'format' | 'messages'>
    & {
    type : 'MAP';

    viewKey : string;

    additionalParameters? : Parameters;
    alignment? : never;
    attributePath? : never;
    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    height : number;
    maxHeight : number;
    required? : never;
    tabOrder? : never;

    actions? : never;
    validators? : never;
    format? : never;
    images? : {};
    messages? : {
        title? : string;
        placeholder? : never;
    };
};
export const mapViewProperties = keys<MapViewProperties>() as string[];
export const mapViewMessages = keys<Required<MapViewProperties>[ 'messages' ]>() as string[];
export const requiredMapViewProperties = keys<RequiredOf<MapViewProperties>>() as string[];

/*** CFG:OverviewView ***/
export type OverviewViewProperties = CommonProperties
    & Omit<FormInheritedProperties, 'infoTextLayout' | 'labelAlignment' | 'labelPosition' | 'labelVisibility' | 'labelWidth'>
    & ContainerCommonInheritedProperties
    & ContainerToContainerInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'alignment' | 'attributePath' | 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'required' | 'tabOrder' | 'validators' | 'format' | 'messages'>
    & {
    type : 'OVERVIEW';

    viewKey : string;

    additionalParameters? : Parameters;
    alignment? : never;
    attributePath? : never;
    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    columns? : ColumnConfig[];
    defaultValue? : never;
    filters? : ColumnFilter[];
    fixFilters? : ColumnFilter[];
    height : number;
    maxHeight : number;
    required? : never;
    sorters? : ColumnSorter[];
    tabOrder? : never;
    toolbarVisibility : Visibility;

    validators? : never;
    format? : never;
    images? : {};
    messages? : {
        title? : string;
        placeholder? : never;
    };
};
export const overviewViewProperties = keys<OverviewViewProperties>() as string[];
export const overviewViewMessages = keys<Required<OverviewViewProperties>[ 'messages' ]>() as string[];
export const requiredOverviewViewProperties = keys<RequiredOf<OverviewViewProperties>>() as string[];


/*** fields ***/
/*** CFG:BooleanField ***/
export type BooleanFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'messages'>
    & {
    type : 'BOOLEAN';

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : boolean;
    triState : boolean;
    useCheckboxLabelAsDefault : boolean;

    messages? : CommonFormFieldProperties[ 'messages' ] & {
        checkboxLabel? : string;
        placeholder? : never;
    };
};
export const booleanFieldProperties = keys<BooleanFieldProperties>() as string[];
export const booleanFieldMessages = keys<Required<BooleanFieldProperties>[ 'messages' ]>() as string[];
export const requiredBooleanFieldProperties = keys<RequiredOf<BooleanFieldProperties>>() as string[];

/*** CFG:ButtonField ***/
export type ButtonFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'attributePath' | 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'images' | 'messages'>
    & {
    type : 'BUTTON',

    attributePath? : never;
    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    iconCls? : string;
    showLoadingSpinner : boolean;

    disableLayoutElementIds? : number[];
    maskLayoutElementIds? : number[];

    images? : CommonFormFieldProperties[ 'images' ] & {
        icon? : string;
    };
    messages? : CommonFormFieldProperties[ 'messages' ] & {
        buttonLabel? : string;
        placeholder? : never;
    };
};
export const buttonFieldProperties = keys<ButtonFieldProperties>() as string[];
export const buttonFieldImages = keys<Required<ButtonFieldProperties>[ 'images' ]>() as string[];
export const buttonFieldMessages = keys<Required<ButtonFieldProperties>[ 'messages' ]>() as string[];
export const requiredButtonFieldProperties = keys<RequiredOf<ButtonFieldProperties>>() as string[];

/*** CFG:ColorField ***/
export type ColorFieldPresentation = 'box' | 'field' | 'none';
export type ColorFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'COLOR',

    defaultValue? : string;
    presentation : ColorFieldPresentation;
};
export const colorFieldProperties = keys<ColorFieldProperties>() as string[];
export const requiredColorFieldProperties = keys<RequiredOf<ColorFieldProperties>>() as string[];

/*** CFG:CoordinateField ***/
export type CoordinateFieldPresentation = 'field' | 'map';
export type CoordinateFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'COORDINATE',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : string;
    height : number;
    mapLayerType : MapLayerType;
    maxHeight : number;
    minHeight : number;
    presentation : CoordinateFieldPresentation;
};
export const coordinateFieldProperties = keys<CoordinateFieldProperties>() as string[];
export const requiredCoordinateFieldProperties = keys<RequiredOf<CoordinateFieldProperties>>() as string[];

/*** CFG:CurrencyField ***/
export type CurrencyFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'CURRENCY',

    defaultValue? : string;
};
export const currencyFieldProperties = keys<CurrencyFieldProperties>() as string[];
export const requiredCurrencyFieldProperties = keys<RequiredOf<CurrencyFieldProperties>>() as string[];

/*** CFG:DateField ***/
export type DateFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'DATE',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : string;
};
export const dateFieldProperties = keys<DateFieldProperties>() as string[];
export const requiredDateFieldProperties = keys<RequiredOf<DateFieldProperties>>() as string[];

/*** CFG:DoubleField ***/
export type DoubleFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'DOUBLE',

    defaultValue? : string;
    precision : number;
};
export const doubleFieldProperties = keys<DoubleFieldProperties>() as string[];
export const requiredDoubleFieldProperties = keys<RequiredOf<DoubleFieldProperties>>() as string[];

/*** CFG:DurationField ***/
export type DurationFieldPrecision = 'day' | 'hour' | 'minute' | 'second' | 'millisecond' | 'microsecond';
export type DurationFieldPresentation = 'input' | 'picker';
export type DurationFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'DURATION',

    defaultValue? : string;
    inputFormat : string;
    precision? : DurationFieldPrecision;
    presentation? : DurationFieldPresentation;
    stepWidth : number;
    limitToStepWidth? : boolean;
};
export const durationFieldProperties = keys<DurationFieldProperties>() as string[];
export const requiredDurationFieldProperties = keys<RequiredOf<DurationFieldProperties>>() as string[];

/*** CFG:EmailField ***/
export type EmailFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'EMAIL',

    defaultValue? : string;
    linkButton? : boolean;
};
export const emailFieldProperties = keys<EmailFieldProperties>() as string[];
export const requiredEmailFieldProperties = keys<RequiredOf<EmailFieldProperties>>() as string[];

/*** CFG:EnumField ***/
export type EnumFieldListingOrder =
    'order'
    | 'asc'
    | 'desc'
    | 'keyAscValueAsc'
    | 'keyAscValueDesc'
    | 'keyDescValueAsc'
    | 'keyDescValueDesc'
    | 'valueAscKeyAsc'
    | 'valueAscKeyDesc'
    | 'valueDescKeyAsc'
    | 'valueDescKeyDesc';
export type EnumFieldPresentation = 'chips' | 'fieldList' | 'fieldList_reversed' | 'grid' | 'grid_reversed';
export type EnumFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'styles'>
    & {
    type : 'ENUM',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    extensible : boolean;
    height : number;
    keyFieldWidth : number;
    listingOrder : EnumFieldListingOrder;
    maxHeight : number;
    presentation : EnumFieldPresentation;
    valueFieldWidth : number;
    fields? : {
        key? : ColorFieldProperties
            | CoordinateFieldProperties
            | CurrencyFieldProperties
            | DateFieldProperties
            | DoubleFieldProperties
            | DurationFieldProperties
            | EmailFieldProperties
            | IbanFieldProperties
            | IntegerFieldProperties
            | LinkFieldProperties
            | PhoneFieldProperties
            | StringFieldProperties
            | TimeFieldProperties
            | TimestampFieldProperties;
        value? : BooleanFieldProperties
            | ColorFieldProperties
            | CoordinateFieldProperties
            | CurrencyFieldProperties
            | DateFieldProperties
            | DoubleFieldProperties
            | DurationFieldProperties
            | EmailFieldProperties
            | IbanFieldProperties
            | ImageDataFieldProperties
            | IntegerFieldProperties
            | JsonFieldProperties
            | LinkFieldProperties
            | PhoneFieldProperties
            | StringFieldProperties
            | TextFieldProperties
            | TimeFieldProperties
            | TimestampFieldProperties;
    }

    /**
     * Beside the styles every form field has, an enum field styles the parts of a single entry:
     * `keyStyle` and `valueStyle` reach the key resp. the value of the entry, `rowStyle` the whole
     * entry ( the row in a field list or grid, the chip in the chips presentation ).
     *
     * Their conditions are resolved **per entry**: the alias of the field's own DataDescriptor
     * carries the value of the entry, the sibling alias ending in `:key` its key - so a condition
     * on `property.<name>` asks for the value, one on `property.<name>.key` for the key,
     * whichever of the three styles it belongs to.
     */
    styles? : {
        style? : Style[];
        labelStyle? : Style[];
        keyStyle? : Style[];
        valueStyle? : Style[];
        rowStyle? : Style[];
    };
};
export const enumFieldProperties = keys<EnumFieldProperties>() as string[];
export const enumFieldStyles = keys<Required<EnumFieldProperties>[ 'styles' ]>() as string[];
export const requiredEnumFieldProperties = keys<RequiredOf<EnumFieldProperties>>() as string[];

/*** CFG:FileField ***/
export type FileFieldListingOrder = 'fileNameAsc' | 'fileNameDesc' | 'fileSizeAsc' | 'fileSizeDesc' | 'uploadDateAsc' | 'uploadDateDesc';
export type FileFieldPresentation = 'chips' | 'fieldList' | 'grid';
export type FileFieldPreview = 'expanded' | 'collapsed' | 'disabled';
export type FileFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'FILE',

    acceptedFiles? : string;
    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    height : number;
    listingOrder : FileFieldListingOrder;
    maxFileSize : number;
    maxHeight : number;
    minHeight : number;
    presentation : FileFieldPresentation;
    preview : FileFieldPreview;
};
export const fileFieldProperties = keys<FileFieldProperties>() as string[];
export const requiredFileFieldProperties = keys<RequiredOf<FileFieldProperties>>() as string[];

/*** CFG:IbanField ***/
export type IbanFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'IBAN',

    defaultValue? : string;
};
export const ibanFieldProperties = keys<IbanFieldProperties>() as string[];
export const requiredIbanFieldProperties = keys<RequiredOf<IbanFieldProperties>>() as string[];

/*** CFG:ImageDataField ***/
export type ImageDataFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'IMAGEDATA',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : string;
    //editButton?: boolean;
    height : number;
    imageHeight? : number;
    imageWidth? : number;
    maxHeight : number;
    maxImageFileSize : number;
    //preview: string;  // discuss
};
export const imageDataFieldProperties = keys<ImageDataFieldProperties>() as string[];
export const requiredImageDataFieldProperties = keys<RequiredOf<ImageDataFieldProperties>>() as string[];

/*** CFG:IntegerField ***/
export type IntegerFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'INTEGER',

    defaultValue? : string;
};
export const integerFieldProperties = keys<IntegerFieldProperties>() as string[];
export const requiredIntegerFieldProperties = keys<RequiredOf<IntegerFieldProperties>>() as string[];

/*** CFG:JsonField ***/
export type JsonFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'JSON',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : string;
    jsonSchema? : string;
    //editButton?: boolean;
    height : number;
    maxHeight : number;
    uiSchema? : string;
};
export const jsonFieldProperties = keys<JsonFieldProperties>() as string[];
export const requiredJsonFieldProperties = keys<RequiredOf<JsonFieldProperties>>() as string[];

/*** CFG:LinkField ***/
export type LinkFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'LINK',

    defaultValue? : string;
    linkButton? : boolean;
};
export const linkFieldProperties = keys<LinkFieldProperties>() as string[];
export const requiredLinkFieldProperties = keys<RequiredOf<LinkFieldProperties>>() as string[];

/*** CFG:PasswordField ***/
export type PasswordFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'messages'>
    & {
    type : 'PASSWORD',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;

    messages? : CommonFormFieldProperties[ 'messages' ] & {
        placeholder? : never;
    };
};
export const passwordFieldProperties = keys<PasswordFieldProperties>() as string[];
export const passwordFieldMessages = keys<Required<PasswordFieldProperties>[ 'messages' ]>() as string[];
export const requiredPasswordFieldProperties = keys<RequiredOf<PasswordFieldProperties>>() as string[];

/*** CFG:PhoneField ***/
export type PhoneFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'PHONE',

    defaultValue? : string;
    linkButton? : boolean;
};
export const phoneFieldProperties = keys<PhoneFieldProperties>() as string[];
export const requiredPhoneFieldProperties = keys<RequiredOf<PhoneFieldProperties>>() as string[];

/*** CFG:ReferenceField ***/
export type ReferenceFieldLinkTarget = 'panel' | 'popup';
export type ReferenceFieldPresentation = 'dropdown' | 'checkboxes';
export type ReferenceFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteQuery' | 'defaultValue' | 'messages'>
    & {
    type : 'REFERENCE',

    addExisting : boolean;
    addNew : boolean;
    allowedItemTypes? : string[];
    autoComplete? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    deleteButton? : boolean;
    linkButton : boolean;
    linkTarget : ReferenceFieldLinkTarget;
    limitToSingleSelection? : boolean;
    orderBy? : string;
    panelConfigFilterPrefix?: string;
    presentation : ReferenceFieldPresentation;
    presentationColumns : number;

    query? : Query;
    getDataAction? : Action;
    additionalParameters? : Parameters;

    editPanelConfigIds? : number[];

    messages? : CommonFormFieldProperties[ 'messages' ] & {
        itemLabel? : string;
        popupTitle? : string;
    };
};
export const referenceFieldProperties = keys<ReferenceFieldProperties>() as string[];
export const referenceFieldMessages = keys<Required<ReferenceFieldProperties>[ 'messages' ]>() as string[];
export const requiredReferenceFieldProperties = keys<RequiredOf<ReferenceFieldProperties>>() as string[];

/*** CFG:StaticContentField ***/
export type StaticContentFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue' | 'required' | 'tabOrder' | 'images' | 'messages'>
    & {
    type : 'STATIC',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : never;
    required? : never;
    tabOrder? : never;

    query? : Query;
    getDataAction? : Action;
    additionalParameters? : Parameters;

    images? : CommonFormFieldProperties[ 'images' ] & {
        content? : string;
    };
    messages? : CommonFormFieldProperties[ 'messages' ] & {
        content? : string;
    };
};
export const staticContentFieldProperties = keys<StaticContentFieldProperties>() as string[];
export const staticContentFieldImages = keys<Required<StaticContentFieldProperties>[ 'images' ]>() as string[];
export const staticContentFieldMessages = keys<Required<StaticContentFieldProperties>[ 'messages' ]>() as string[];
export const requiredStaticContentFieldProperties = keys<RequiredOf<StaticContentFieldProperties>>() as string[];

/*** CFG:StringField ***/
export type StringFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'defaultValue'>
    & {
    type : 'STRING',

    defaultValue? : string;
};
export const stringFieldProperties = keys<StringFieldProperties>() as string[];
export const requiredStringFieldProperties = keys<RequiredOf<StringFieldProperties>>() as string[];

/*** CFG:TextField ***/
export type TextFieldEditMode = 'html' | 'plaintext';
export type TextFieldToolbarVisibility = 'hidden' | 'visible' | 'whileEditing';
export type TextFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'TEXT',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : string;
    //editButton?: boolean;
    editMode : TextFieldEditMode;
    height : number;
    maxHeight : number;
    toolbarVisibility : TextFieldToolbarVisibility;
};
export const textFieldProperties = keys<TextFieldProperties>() as string[];
export const requiredTextFieldProperties = keys<RequiredOf<TextFieldProperties>>() as string[];

/*** CFG:TimeField ***/
export type TimeFieldPrecision = 'minutes' | 'seconds' | 'milliseconds' | 'microseconds';
export type TimeFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'TIME',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : string;
    precision? : TimeFieldPrecision;
};
export const timeFieldProperties = keys<TimeFieldProperties>() as string[];
export const requiredTimeFieldProperties = keys<RequiredOf<TimeFieldProperties>>() as string[];

/*** CFG:TimestampField ***/
export type TimestampFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'TIMESTAMP',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : string;
    precision? : TimeFieldPrecision;
    timezone? : string;
};
export const timestampFieldProperties = keys<TimestampFieldProperties>() as string[];
export const requiredTimestampFieldProperties = keys<RequiredOf<TimestampFieldProperties>>() as string[];

/*** CFG:ValueSetField ***/
export type ValueSetListingOrder = 'order' | 'asc' | 'desc';
export type ValueSetFieldPresentation = 'dropdown' | 'radio' | 'button';
export type ValueSetFieldProperties =
    CommonProperties
    & FormInheritedProperties
    & ContainerCommonInheritedProperties
    & ContainerToFormFieldInheritedProperties
    & Omit<CommonFormFieldProperties, 'autoComplete' | 'autoCompleteDelay' | 'autoCompleteMinLength' | 'autoCompleteQuery' | 'defaultValue'>
    & {
    type : 'VALUESET',

    autoComplete? : never;
    autoCompleteDelay? : never;
    autoCompleteMinLength? : never;
    autoCompleteQuery? : never;
    defaultValue? : string;
    blacklist? : string;
    listingOrder? : ValueSetListingOrder;
    presentation? : ValueSetFieldPresentation;
    radioColumns? : number;
    valueSetKey? : string;
    valueSetId? : number;
    whitelist? : string;
};
export const valueSetFieldProperties = keys<ValueSetFieldProperties>() as string[];
export const requiredValueSetFieldProperties = keys<RequiredOf<ValueSetFieldProperties>>() as string[];

export type FormFieldProperties = CalendarViewProperties
    | ChartViewProperties
    | GraphViewProperties
    | MapViewProperties
    | OverviewViewProperties

    | BooleanFieldProperties
    | ButtonFieldProperties
    | ColorFieldProperties
    | CoordinateFieldProperties
    | CurrencyFieldProperties
    | DateFieldProperties
    | DoubleFieldProperties
    | DurationFieldProperties
    | EmailFieldProperties
    | EnumFieldProperties
    | FileFieldProperties
    | IbanFieldProperties
    | ImageDataFieldProperties
    | IntegerFieldProperties
    | JsonFieldProperties
    | LinkFieldProperties
    | PasswordFieldProperties
    | PhoneFieldProperties
    | ReferenceFieldProperties
    | StaticContentFieldProperties
    | StringFieldProperties
    | TextFieldProperties
    | TimeFieldProperties
    | TimestampFieldProperties
    | ValueSetFieldProperties;

/*** VariableField ***/
type PossibleVariableFieldProperties = Exclude<FormFieldProperties, ButtonFieldProperties | EnumFieldProperties | FileFieldProperties | ReferenceFieldProperties | StaticContentFieldProperties>;
export type VariableFieldProperties = Omit<PossibleVariableFieldProperties,
        'attributePath' | 'defaultValue' | 'type'>
    & {
    type : 'VARIABLE';

    attributePath? : never;
    defaultValue? : never;

    variableType : PossibleVariableFieldProperties['type'];
    variableName : string;
};
export const variableFieldVariableTypes = enumerate<PossibleVariableFieldProperties['type']>()


/*** mappings ********************************************************************************************************/

type ValueTypeToFieldTypeMapping = {
    [valueType in PropertyValueType] : ( FormFieldProperties['type'] )[];
};
export const valueTypeToFieldTypeMapping : ValueTypeToFieldTypeMapping = {
    BOOLEAN   : [ 'BOOLEAN' ],
    COLOR     : [ 'COLOR', 'VALUESET' ],
    COORDINATE: [ 'COORDINATE', 'VALUESET' ],
    CURRENCY  : [ 'CURRENCY', 'DOUBLE', 'VALUESET' ],
    DATE      : [ 'DATE', 'VALUESET' ],
    DOUBLE    : [ 'DOUBLE', 'CURRENCY', 'INTEGER', 'VALUESET' ],
    DURATION  : [ 'DURATION', 'DOUBLE', 'INTEGER', 'VALUESET' ],
    EMAIL     : [ 'EMAIL', 'VALUESET' ],
    IBAN      : [ 'IBAN', 'VALUESET' ],
    IMAGEDATA : [ 'IMAGEDATA', 'VALUESET' ],
    INTEGER   : [ 'INTEGER', 'CURRENCY', 'VALUESET' ],
    JSON      : [ 'JSON', 'TEXT' ], // TODO: remove TEXT
    LINK      : [ 'LINK', 'VALUESET' ],
    PHONE     : [ 'PHONE', 'VALUESET' ],
    STRING    : [ 'STRING', 'EMAIL', 'IBAN', 'IMAGEDATA', 'LINK', 'PASSWORD', 'PHONE', 'VALUESET' ],
    TEXT      : [ 'TEXT', 'EMAIL', 'IBAN', 'IMAGEDATA', 'JSON', 'LINK', 'PASSWORD', 'PHONE', 'STRING', 'VALUESET' ],
    TIME      : [ 'TIME', 'VALUESET' ],
    TIMERULE  : [ 'STRING', 'TEXT' ],
    TIMESTAMP : [ 'TIMESTAMP', 'VALUESET' ]
};

type FieldTypeToItemTypeMapping = {
    [fieldType in ( Exclude<FormFieldProperties['type'], | 'VARIABLE'> | 'TIMERULE' )] : string;
};
export const fieldTypeToItemTypeMapping : FieldTypeToItemTypeMapping = {
    CALENDAR: 'CalendarView',
    CHART   : 'ChartView',
    GRAPH   : 'GraphView',
    MAP     : 'MapView',
    OVERVIEW: 'OverviewView',

    BOOLEAN   : 'BooleanField',
    BUTTON    : 'ButtonField',
    COLOR     : 'ColorField',
    COORDINATE: 'CoordinateField',
    CURRENCY  : 'CurrencyField',
    DATE      : 'DateField',
    DOUBLE    : 'DoubleField',
    DURATION  : 'DurationField',
    EMAIL     : 'EmailField',
    ENUM      : 'EnumField',
    FILE      : 'FileField',
    IBAN      : 'IbanField',
    IMAGEDATA : 'ImageDataField',
    INTEGER   : 'IntegerField',
    JSON      : 'JsonField',
    LINK      : 'LinkField',
    PASSWORD  : 'PasswordField',
    PHONE     : 'PhoneField',
    REFERENCE : 'ReferenceField',
    STATIC    : 'StaticContentField',
    STRING    : 'StringField',
    TEXT      : 'TextField',
    TIME      : 'TimeField',
    TIMERULE  : 'TimeruleField',
    TIMESTAMP : 'TimestampField',
    VALUESET  : 'ValueSetField'
};

type LayoutElementTypeToItemTypeMapping = FieldTypeToItemTypeMapping & {
    CONTAINER : string;
    FORM : string;
    TABCONTAINER : string;
    VARIABLE : string;
    TIMERULE : string;
};
export const layoutElementTypeToItemTypeMapping : LayoutElementTypeToItemTypeMapping = {
    ...fieldTypeToItemTypeMapping,
    CONTAINER   : 'Container',
    FORM        : 'Form',
    TABCONTAINER: 'TabContainer',
    VARIABLE    : 'StringField',
    TIMERULE    : 'TextField'
};

export const layoutElementItemTypeIcons : {
    [ fieldType : string ] : string
} = {
    BooleanField      : 'fa-regular fa-square-check',
    ButtonField       : 'fa-solid fa-mobile-button fa-rotate-90',
    ColorField        : 'fa-solid fa-palette',
    Container         : 'fa-regular fa-rectangle-list',
    CoordinateField   : 'fa-solid fa-map-location-dot',
    CurrencyField     : 'fa-solid fa-euro-sign',
    DateField         : 'fa-regular fa-calendar',
    DoubleField       : 'fa-solid fa-divide',
    DurationField     : '',
    EmailField        : 'fa-solid fa-at',
    EnumField         : 'fa-solid fa-arrow-down-1-9',
    FileField         : 'fa-regular fa-file',
    IbanField         : 'fa-solid fa-building-columns',
    ImageDataField    : 'fa-regular fa-image',
    IntegerField      : 'fa-solid fa-one',
    JsonField         : 'fa-solid fa-code',
    LinkField         : 'fa-solid fa-link',
    PasswordField     : 'fa-solid fa-asterisk',
    PhoneField        : 'fa-solid fa-phone',
    ReferenceField    : 'fa-solid fa-retweet',
    Separator         : 'fa-solid fa-minus',
    StaticContentField: 'fa-solid fa-font',
    StringField       : 'fa-regular fa-closed-captioning',
    TabContainer      : 'fas fa-table-columns',
    TextField         : 'fa-solid fa-text-height',
    TimeField         : 'fa-regular fa-clock',
    TimeruleField     : 'fa-solid fa-timeline',
    TimestampField    : 'fa-regular fa-bell',
    ValueSetField     : 'fa-solid fa-list-check'
};


/*** method types ****************************************************************************************************/

export type LayoutElements = {
    [ layoutElementId : number ] : LoadFormData | LoadContainerData | LoadTabContainerData | FormFieldData;
};

export type FormFieldData = {
    type : PropertyValueType | 'CALENDAR' | 'CHART' | 'GRAPH' | 'MAP' | 'OVERVIEW' | 'BUTTON' | 'ENUM' | 'FILE' | 'PASSWORD' | 'REFERENCE' | 'STATIC' | 'VALUESET' | 'VARIABLE';

    data : FormFieldProperties | VariableFieldProperties;
    dataDescriptorAlias? : string;
    additionalDataDescriptorAliases? : string[];
};
export type FormFieldLayoutStructure = FormFieldData;
export type FormFieldLayoutStructureAndDataBinding = {
    type : FormFieldData[ 'type' ];

    layoutElementId : number;
    dataDescriptorAlias? : string;
};

export type ContainerData = {
    type : 'CONTAINER';

    data : ContainerProperties;
};
export type LoadContainerData = {
    type : 'CONTAINER';

    data : LoadContainerProperties;
    isRepeatedForDataDescriptorAlias? : string;
};
export type ContainerLayoutStructure = {
    type : 'CONTAINER';

    layoutElementId : number;
    children : ( FormFieldLayoutStructureAndDataBinding | ContainerLayoutStructure | TabContainerLayoutStructure )[];
};
export type ContainerLayoutStructureAndDataDescriptors = {
    containerLayoutStructure : ContainerLayoutStructure;
    dataDescriptors : DataDescriptors;
    layoutElements : LayoutElements;
}

export type TabContainerData = {
    type : 'TABCONTAINER';

    data : TabContainerProperties;
};
export type LoadTabContainerData = {
    type : 'TABCONTAINER';

    data : LoadTabContainerProperties;
};
export type TabContainerLayoutStructure = {
    type : 'TABCONTAINER';

    layoutElementId : number;
    children : ContainerLayoutStructure[];
};
export type TabContainerLayoutStructureAndDataDescriptors = {
    containerLayoutStructure : TabContainerLayoutStructure;
    dataDescriptors : DataDescriptors;
    layoutElements : LayoutElements;
}

export type FormData = {
    type : 'FORM',

    data : FormProperties;
};
export type LoadFormData = {
    type : 'FORM',

    data : LoadFormProperties;
    dataStructure? : DataStructure;
};
export type FormLayoutStructure = {
    type : 'FORM',

    layoutElementId : number;
    children : ContainerLayoutStructure[];
};
export type FormLayoutStructureAndDataDescriptors = {
    formLayoutStructure : FormLayoutStructure;

    dataDescriptors : DataDescriptors;
    layoutElements : LayoutElements;
};

export type RequestConfig = {
    requestCommand? : string;
    requestModule? : string;
    requestParams? : {
        [ key : string ] : string
    };
};

/*** loadConfig ***/
export type LoadConfigParameters = Partial<BaseRequestParameters> & ( {
    viewKey : string;
    configHash? : string;
    rootItemId : number;
    rootItemKey? : never;
    rootItemType? : never;
} | {
    viewKey : string;
    configHash? : string;
    rootItemId? : never;
    rootItemKey : string;
    rootItemType : string;
} );
export type LoadConfigDataParameters = Partial<BaseRequestParameters> & ( {
    viewId : string;
    viewKey : string;
} );
export type LoadConfigResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];

        viewName : string;
        title : string;
        tabTitle? : string;
        tooltipTitle? : string;
        iconCls? : string;
        configHash : string;
        exportConfig? : RequestConfig;
        layoutStructure : FormLayoutStructure;
        layoutElements : LayoutElements;
        dataStructure? : DataStructure;
        dataDescriptors? : DataDescriptors;
        saveConfig? : RequestConfig;
        showToolbar? : boolean;
    };
    metaData : {
        metrics : Metric[];

        // debug
        configCypher? : string;
        configSql? : string;
        configParameters? : {
            [ parameter : string ] : any
        };
        cypher? : string;
        sql? : string;
        parameters? : {
            [ parameter : string ] : any
        };
    };
};
export type LoadConfigDataResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
        references : ReferenceAttributes[];
        rootItemId : number;
    };
    metaData : {
        metrics : Metric[];

        // debug
        cypher? : string;
        sql? : string;
        parameters? : {
            [ parameter : string ] : any
        };
    };
};

/*** loadData ***/
export type LoadDataParameters = Partial<BaseRequestParameters> & ( {
    viewKey : string;
    configHash? : string;
    verificationHash? : string;
    rootItemId : number;
    rootItemKey? : never;
    rootItemType? : never;
} | {
    viewKey : string;
    configHash? : string;
    verificationHash? : string;
    rootItemId? : never;
    rootItemKey : string;
    rootItemType : string;
} );
export const loadDataParameterDefaults = {};
export type LoadDataResult = {
    success : boolean;
    data : {
        items : LoadedItemData[];
        references : LoadedReferenceData[];

        rootItemId : number;

        viewName : string;
        title : string;
        tabTitle? : string;
        tooltipTitle? : string;
        iconCls? : string;
        configHash : string;
        exportConfig? : RequestConfig;
        layoutStructure? : FormLayoutStructure;
        layoutElements? : LayoutElements;
        dataStructure? : DataStructure;
        dataDescriptors? : DataDescriptors;
        saveConfig? : RequestConfig;
        showToolbar? : boolean;
    };
    metaData : {
        metrics : Metric[];

        // debug
        configCypher? : string;
        configSql? : string;
        configParameters? : {
            [ parameter : string ] : any
        };
        cypher? : string;
        sql? : string;
        parameters? : {
            [ parameter : string ] : any
        };
    };
};

/*** clearConfig ***/
export type ClearConfigParameters = {
    configHash? : string;
};
export type ClearConfigResult = {
    success : boolean;
    metaData : {
        metrics : Metric[];
    }
};


/*** loadData ***/
export type LoadDataForDataStructureParameters = Partial<BaseRequestParameters> & ( {
    dataStructure : DataStructure;
    rootItemId : number;
    rootItemKey? : never;
    rootItemType? : never;
} | {
    dataStructure : DataStructure;
    rootItemId? : never;
    rootItemKey : string;
    rootItemType : string;
} );
export type LoadDataForDataStructureResult = {
    success : boolean;
    data : {
        items : LoadedItemData[];
        references : LoadedReferenceData[];
    };
    metaData : {
        metrics : Metric[];

        payloadSchema : Schema;

        // debug
        cypher? : string;
        sql? : string;
        parameters? : {
            [ parameter : string ] : any
        };
    };
};


/*** loadSearchItemFieldItems ***/
export type LoadSearchItemFieldItemsParameters = BaseRequestParameters & LoadFinderOverviewDataParameters;
export type LoadSearchItemFieldItemsResult = LoadFinderOverviewDataResult;

/*** loadReferenceFieldItems ***/
export type LoadReferenceFieldItemsParameters = Partial<BaseRequestParameters> & {
    itemTypeName : string;
    itemTypeNames : string[];
    searchString? : string;
    orderBy? : string;
    query? : {
        id : number;
        parameters : {
            [ parameter : string ] : string;
        };
    };
    searchTemplate? : string;
    start? : number;
    limit? : number;
};
export type LoadReferenceFieldItemsResult = {
    success : boolean;
    data : {
        items : ItemAttributes[];
    };
    metaData : {
        total : number;
        metrics : Metric[];

        // debug
        query? : string;
        cypher? : string;
        sql? : string;
        parameters? : {
            [ parameter : string ] : any
        };
    };
};


export type GetFormItemIdByDisplayAttributeParameters = {
    displayAttributeValue : string;
};
export type GetFormItemIdByDisplayAttributeResult = {
    success : true;
    data : {
        rootItemId? : number;
    };
    metaData : {
        metrics : Metric[];

        // debug
        getFormItemId? : {
            sql? : string;
            parameters? : {
                [ parameter : string ] : any
            };
        };
    };
};


export type GetViewKeyAndRootItemIdByNameAndDisplayAttributeParameters = {
    viewName : string;
    displayAttributeValue : string;
};
export type GetViewKeyAndRootItemIdByNameAndDisplayAttributeResult = {
    success : true;
    data : {
        viewKey? : string;
        rootItemId? : number;
        status? : string;
    };
    metaData : {
        metrics : Metric[];

        // debug
        getFormKey? : {
            sql? : string;
            parameters? : {
                [ parameter : string ] : any
            };
        };
        getFormRootDataObject? : {
            cypher? : string;
            sql? : string;
            parameters? : {
                [ parameter : string ] : any
            };
        };
        getRootItemId? : {
            sql? : string;
            parameters? : {
                [ parameter : string ] : any
            };
        };
    };
};


/*** deleteForm ***/
export type DeleteFormParameters = {
    viewId : number;
    historizeOnly? : boolean;
};
export type DeleteFormResult = {
    success : boolean;
    metaData : {
        metrics : Metric[];
    }
};
